// janus-engine/statuses/expiry.ts — ONE status-expiry walk over N rows, shared by T1 (end turn: the ending actor's END
// boundary + the next actor's START boundary) and T4 (the GM time push: the CALENDAR boundary over every row the GM holds).
//
// PURE. Mirrors, family by family, the four legacy passes (plan §1):
//   END   — check_statuses_for_this_encounter_creature_id_as_a_caster (caster rows: status_ends_on_caster_end_of_next_turn
//           AND the campaign clock ≥ the row's end time) + ..._as_a_target (target rows: status_ends_on_target_end_of_turn;
//           the calendar rule minus the caster / target-start flags; the campaign-handled ids are SKIPPED by legacy).
//   START — ..._next_encounter_creature_id_as_a_caster (caster rows: status_ends_on_caster_start_of_next_turn — every
//           connection, every target) + ..._as_a_target (target rows: status_ends_on_target_start_of_turn; the calendar
//           rule minus the caster / target-end flags; no_actions / no_reactions overrides on the SURVIVING rows).
//   CALENDAR — the target-side calendar rule alone (T4).
// Every branch requiring a separate effect plan is a GATE (a reason string): DoT ticks, contest
// retries, transitional saves, dying, regeneration, maintained upkeep, the out-of-breath handlers, zone anchors, shapeshift
// teardown, loss triggers on the loser, placement / summon / charge link tails, incomplete peer closures,
// and any definition whose layers compile into a cluster the frame cannot re-derive (§3.1 allowlist).
// Gates read state only; callers serve the boundary and disclose unsupported effects. Due zone anchors are consumed by
// the observing GM's shared connection cascade rather than by this plain-expiry frame.

import { NEXT_SPELL_LAYERS } from '../sanguine/next-spell-layers.js';
import { isWaterWet } from '../movement/water-wet.js';
import { projectStatusDefenseRemoval, STATUS_DEFENSE_LAYERS } from '../compile/status-defense-removal.js';
import { readStatusCasterId, statusConnectionPeers, storedStatusRows } from '../compile/creature-status-reader.js';
import { statusOriginDefinitionId } from './status-origin.js';
import { isAttackPassiveWindow, isDamageOutputStatus, changesStatusAttackBudget, projectStatusAttackBudget, type StatusAttackBudget } from '../compile/status-attack-budget.js';
import { encounterCreatureField } from '../../shared/array-types.js';
import { readItemField } from '../compile/item-field-reader.js';
import { statusRemovalRuntime } from '../compile/status-removal-runtime.js';
import { isTimedPassiveSpeedLayer } from '../compile/combat-start-timed-passive.js';
import { isCastingOnlyStatusLayer, statusRuntimeLayerKeys } from './runtime-layers.js';
import { STATUS_ACTION_RESIDUAL_LAYERS } from '../compile/status-residue-layers.js';
import { HandBack } from '../ability/hand-back.js';
import { readInventoryField } from '../compile/inventory-raw-readers.js';
import { runtimeOnlyStatusPassives, STATUS_RESOURCE_GUARD_LAYERS } from '../compile/status-runtime-passives.js';
type Row = Record<string, unknown>;

export const DOT_ROLL_TYPE = '77A044B7-7041-CA96-3C63-BB718B15A364';
export const REGENERATION_ROLL_TYPE = '9909BE44-99AE-8780-BB12-BD99BA6E672E';
export const TEMP_REGENERATION_ROLL_TYPE = '4634635E-4E82-DFB3-8411-27A6BFB8CF0F';
/** StatusEffect catalog ids (item_id precedes variable_name in the PHP LIST; read with that order). */
export const STATUS_IDS = {
  dying: '8AE129AF-52D8-CCE8-706A-85767DB52740',
  suffocating: '2A20ACDF-3BC6-B964-8FBD-F8E1AC581AA5',
  drowning: '1E8008FA-B68A-8DE8-48CE-0EB121008FEB',
  petrifying: 'DFCF49C0-D5BC-C56F-A7C5-DC8D134ED7D7',
  riding: 'FF8D219A-2A83-0EE5-A222-E748753E8D0B',
  ridden: 'F13F6A58-FE91-F24D-12BE-BE7CCAD81A40',
  mounted: '680EFB9E-3CFB-491F-A620-5BABF800131C',
  piloting: 'A1F1FAF2-DA1E-D537-36C2-2B74DC82CB12',
  piloted: 'DDC66C31-4093-84AF-18BB-093D924B8738',
  grappling: 'E4A34093-AAAB-6453-BCE0-FBA65EE263AD',
  grappled: '0C2A8037-B0BC-8300-C818-198881B00E7D',
  restrained: 'E34AF232-5438-88FC-9A9F-73850CCCFD51',
  concentrating: 'E7FDB796-26DA-E0F5-8F88-C21FE01D923B',
  summon_tag: 'B84EBE3F-B385-4CEF-9C69-648086C4CB7D',
  holding_mastery: 'EEDD357F-C1CA-44C1-A53F-DD06EEFBA597',
  performing_a_melody: 'D09D563D-0689-4B56-AEAA-5A254DC408A4',
  casting: '5D939B6E-B9BC-7AC5-5143-4F00F4189EA3',
  casting_ritual_spell: '5833A733-D277-0A60-3A94-A0F355A79A80',
  gathering: '3F171782-CC56-32DF-81B4-8C0ED89F47F4',
  crafting: '62ED7394-9C33-D41A-C426-1BC6723C999A',
  lockpicking: 'FF9A38A3-5F35-F083-E153-7DF41B0C0826',
  donning: 'D343892B-338C-4FBE-BEA0-2015FE503F43',
  doffing: '398DD6E8-AFAE-EA0D-6FE9-C22842D53775',
  disenchanting: '1C4D81BB-FB79-68AD-E881-28D874927FD2',
  reading: 'ED30CCB0-1645-B97E-B9A6-867D8D0094BD',
  training: '426C7E0B-EF44-498C-10B2-B50A9E28E45C',
  resting: '3188FB3B-C860-43D4-9BA0-40364C460385',
} as const;
/** push_time_out_of_combat_status_effect_logic owns these; both turn passes `continue` past them. */
export const CAMPAIGN_HANDLED_STATUS_IDS = new Set<string>([STATUS_IDS.casting, STATUS_IDS.casting_ritual_spell, STATUS_IDS.gathering, STATUS_IDS.crafting, STATUS_IDS.lockpicking, STATUS_IDS.donning, STATUS_IDS.doffing, STATUS_IDS.disenchanting, STATUS_IDS.reading, STATUS_IDS.training, STATUS_IDS.resting]);
/** Link identities need a complete connection, rather than an independent raw expiry. */
export const LINK_STATUS_IDS = new Set<string>([STATUS_IDS.riding, STATUS_IDS.ridden, STATUS_IDS.mounted, STATUS_IDS.piloting, STATUS_IDS.piloted, STATUS_IDS.grappling, STATUS_IDS.grappled, STATUS_IDS.restrained, STATUS_IDS.concentrating, STATUS_IDS.summon_tag, STATUS_IDS.holding_mastery, STATUS_IDS.performing_a_melody]);
// These complete closures have no placement, summon-departure or charge-release tail.
const EXPIRY_LINK_STATUS_IDS = new Set<string>([STATUS_IDS.concentrating, STATUS_IDS.grappling, STATUS_IDS.grappled, STATUS_IDS.restrained]);
/** StatusEffect::LIST `transitional` entries (Petrifying → Petrified). PHP twin: ft_turn_truth_transitional_ids(). */
export const TRANSITIONAL_STATUS_IDS = new Set<string>([STATUS_IDS.petrifying]);
/** Intake effect layers with no retained compiled contribution. Audited runtime additions follow;
 * retained compiled contributions (speeds, AC, senses, essences, size, defenses, attacks, …) still need their own removal lane. */
const BASE_PLAIN_DEFINITION_LAYERS = new Set([
  'duration_int', 'duration_type', 'target_type', 'do_not_show_ability', 'requires_in_combat', 'unenchantable',
  'effect_ends_on_start_of_caster_next_turn', 'effect_ends_on_end_of_caster_next_turn', 'effect_ends_on_target_end_of_turn', 'effect_ends_on_target_start_of_turn',
  'roll_type_against_adv', 'roll_type_against_dis', 'skill_roll_adv', 'skill_roll_dis', 'skill_rolls_adv', 'skill_rolls_dis',
  'if_caster_has_status_effect_not_active', 'if_caster_has_status_effect_active',
  'ability_resets_long_rest', // Refill metadata: only long rest consumes it; status removal never resets the holder's pool.
  'requires_skill_tier_3', 'triggers_after_combat_begins', // Cast eligibility / future trigger; retiring the status removes the carrier.
  'assistable_status', 'status_effect_pausable', // Status controls have no removal work or compiled contribution.
  'dice', 'die', 'mod', 'damage_type', 'roll_type', // Roll payload, read when a tick/cast runs; expiry itself does not roll it.
  'silenced', 'hit_to_crit', 'ability_does_not_provoke_opportunity_attacks', 'walks_on_water_and_ice', // Live status reads, no compiled residue.
  'limited_visibility_ft', // Status layering does not retain this authoring/visibility payload in a compiled cluster.
  'no_actions', 'no_reactions', 'ap_change_at_start_of_turn',
  'triggers_when_caster_gains_luck', 'triggers_on_next_roll_type', 'triggers_when_caster_turn_begins', 'change_caster_luck', 'roll_type_adv', 'roll_type_dis',
  'spell_school', 'spell_components', 'status_effect_applied_to_caster_after_cast', 'status_effect_applied_to_target_after_cast',
]);
// Additional metadata is inert at removal; light/override reads are projected before retiring the stored row.
const ADDITIONAL_DEFINITION_LAYERS = [
  ...NEXT_SPELL_LAYERS,
  'skill_roll_contester', 'skill_roll_contestee', 'ability_dc',
  'status_effect_applied_to_target_after_contesting_roll_failed', 'status_effect_applied_to_caster_after_contesting_roll_failed',
  'target_retries_contesting_roll_on_end_of_turn', 'status_effect_applied_to_target_on_hit', 'status_effect_applied_to_caster_on_hit', 'stacks_applied',
  'requires_level', 'requires_class', 'requires_class_level', 'requires_subclass',
  'effect_only_affects_opposing_team_type', 'effect_only_affects_same_team_type',
  'ability_only_works_against_size_category_number_below', 'ability_only_works_against_size_category_number_above',
  'if_weapon_classification_equipped_active', 'if_weapon_type_equipped_active', 'attached_equipped_weapon', 'attached_weapon_form',
  'channel_orb', // Cast-time orb creation, not channel_orb_each_turn or an orb-queue teardown.
  'next_spell_mana_discount', // Read from the active status at spell cast time; no retained compiled contribution.
  'bright_light_dim_light', 'override_ability', 'spell_concentration',
  'triggers_on_next_roll_type_against', // The next-roll reader consumes this rider; expiry leaves no compiled trigger residue.
];
export const PLAIN_DEFINITION_LAYERS = /* @__PURE__ */ (() => new Set([...BASE_PLAIN_DEFINITION_LAYERS, ...ADDITIONAL_DEFINITION_LAYERS]))();
export const isPlainDefinitionLayer = (key: string): boolean => isCastingOnlyStatusLayer(key) || PLAIN_DEFINITION_LAYERS.has(key)
  || STATUS_ACTION_RESIDUAL_LAYERS.has(key) || STATUS_RESOURCE_GUARD_LAYERS.has(key) || /^requires_skill_tier_\d+$/.test(key);

/** DELAYED DETONATION (2026-10-07, R-176 — ability/statuses/status-expiry-detonation.ts): a definition carrying damage_on_status_expiry deals its
 * damage when its row expires at the end of the holder's turn (family target_end). Its damage + menta / ability chassis layers compile into no
 * cluster, so they stay plain for THAT definition; the removal is flagged `detonates` and the boundary gates 'expiry-detonation' (a TICK gate). */
export const DAMAGE_ON_STATUS_EXPIRY = 'damage_on_status_expiry';
export const DETONATION_SPLASH_FT = 'damage_on_status_expiry_splash_ft';
export const DETONATION_SPLASH_DICE = 'damage_on_status_expiry_splash_dice';
export const DETONATION_SPLASH_DIE = 'damage_on_status_expiry_splash_die';
export const DETONATION_LAYERS = [DAMAGE_ON_STATUS_EXPIRY, DETONATION_SPLASH_FT, DETONATION_SPLASH_DICE, DETONATION_SPLASH_DIE] as const;
export const DETONATION_CHASSIS_LAYERS: ReadonlySet<string> = new Set<string>([...DETONATION_LAYERS,
  'menta_level', 'menta_mental_manifestation', 'consumes_ability_charges', 'consumes_charges_qty', 'requires_subclass', 'requires_class',
  'requires_class_level', 'spell_casting_time', 'spell_concentration', 'maintained_by_ability', 'maintained_by_qty']);

export type ExpiryBoundary = { kind: 'end'; actorId: string } | { kind: 'start'; actorId: string } | { kind: 'calendar' };
export type ExpiryFamily = 'caster_end' | 'target_end' | 'calendar_end' | 'caster_start' | 'target_start' | 'calendar_start' | 'calendar';
export type ExpiryLogKey = 'status_effect_expired' | 'effect_ends_on_target_end_of_turn' | 'effect_ends_on_target_start_of_turn';

export interface StatusRemoval {
  status_id: string;
  target_id: string;
  caster_id: string;
  item_id: string;
  family: ExpiryFamily;
  log_key: ExpiryLogKey;
  /** Who the legacy log row is BY: the target (target passes, caster-END) or the caster (caster-START). */
  log_by: string;
  /** caster-START's connection-wide family; every admitted member is listed explicitly. */
  all_connections: boolean;
  /** The definition's skill_roll_adv/dis skill ids (the skills cluster must be re-derived on the loser). */
  skill_adv_ids: string[];
  skill_dis_ids: string[];
  /** target_end only: the definition detonates on this expiry (damage_on_status_expiry — the tick lane rolls it). Absent = no. */
  detonates?: true;
  /** Browser-compiled defenses after an immunity-bearing expiry. */
  defenses?: Row;
  attack_budget?: StatusAttackBudget;
}
export interface ExpiryOverrides { no_actions: boolean; no_reactions: boolean }
export interface ExpiryResult {
  /** Lifted peer closures, by status identity. Page-side only; StatusRemoval's wire shape stays unchanged. */
  groups?: string[][];
  removals: StatusRemoval[];
  /** Pre-decision gate reasons (empty = the boundary is within the lane's supported set). */
  gates: string[];
  /** START boundary only: the surviving target rows' scalar overrides (legacy writes aps 0 / reaction 0). */
  overrides: ExpiryOverrides;
}
export interface ExpiryReaders {
  immunityExpiry?: boolean;
  waterWetHeld?: (row: Row) => boolean;
  /** Full definition {items, item_layer_types} for a catalog id; null = not held (a gate, never a fetch). */
  definition: (id: string) => Row | null;
  /** ftLayerValue twin (definition reads). */
  value: (row: Row, name: string) => unknown;
  /** ftLayerEntries twin. */
  entries: (row: Row, name: string) => unknown[];
  /** Absolute campaign seconds of a {year..second} tuple on the server's basis (ftCampaignNowSeconds). */
  seconds: (cols: { year: number; month: number; day: number; hour: number; minute: number; second: number }) => number;
  /** The campaign clock NOW, in the same seconds basis. */
  nowSeconds: number;
  /** Held target/caster row by creature or inventory identity. */
  rowFor: (actorId: string) => Row | null;
  /** Every held target/caster projection, including inventory, for the connected-peer test. */
  allStatusRows: () => Row[];
  /** Held floor and carried inventory targets, including container contents. No inventory has a turn of its own. */
  inventoryRows?: () => Row[];
  /** Non-creature zone anchors held by the page; their calendar expiry belongs to the observing GM. */
  zoneStatusRows?: () => Row[];
  /** Does this row carry a `triggers_when_caster_loses_status_effect` carrier (any ability type / passive / status)? */
  carriesLossTrigger: (row: Row) => boolean;
  /** Vitality of a row (current, max) for the regeneration gate. */
  vitality: (row: Row) => { current: number; max: number };
}

const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const flag = (row: Row, key: string): boolean => Number(scalar(row[key]) ?? 0) !== 0; // the FLAT truthiness read (2026-08-09)
const uuidRe = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;

export function statusRows(row: Row | null, mode: 'target' | 'caster'): Row[] {
  return storedStatusRows(row, mode);
}
export function statusItemId(status: Row): string {
  return upper(scalar(status.status_item_id) ?? (typeof status.status_arr === 'string' ? status.status_arr : null)
    ?? record(status.status_arr)?.item_id ?? record(record(status.status_arr)?.items)?.item_id ?? '');
}
function statusDefinition(status: Row, read: ExpiryReaders): Row | null {
  const nested = record(status.status_arr);
  return nested && (record(nested.item_layer_types) || record(nested.items)) ? nested : read.definition(statusItemId(status));
}
/** status_instance_has_campaign_pause twin: any non-zero paused clock field. */
export function statusPaused(status: Row): boolean {
  return ['year', 'month', 'day', 'hour', 'minute', 'second'].some(part => flag(status, `status_paused_campaign_${part}`));
}
/** A due, unpaused Concentrating row on the out-of-combat calendar. Legacy's calendar pass ended the spell with it, so the observing
 * GM's connection cascade retires the whole connection (gm-boundary.ts dueCascadeSources, same test); the calendar walk leaves it alone. */
function isDueConcentration(status: Row, read: Pick<ExpiryReaders, 'seconds' | 'nowSeconds'>): boolean {
  if (statusItemId(status) !== STATUS_IDS.concentrating || !upper(scalar(status.status_connected_id)) || statusPaused(status)) return false;
  const end = statusEndSeconds(status, read);
  return end !== null && end <= read.nowSeconds;
}
/** The row's end time as absolute seconds, or null when it has no end (all six zero / absent). */
export function statusEndSeconds(status: Row, read: Pick<ExpiryReaders, 'seconds'>): number | null {
  const cols = { year: 0, month: 0, day: 0, hour: 0, minute: 0, second: 0 };
  let any = false;
  for (const part of Object.keys(cols) as Array<keyof typeof cols>) {
    const v = Number(scalar(status[`status_ends_campaign_${part}`]) ?? 0);
    cols[part] = Number.isFinite(v) ? v : 0;
    if (cols[part] > 0) any = true;
  }
  return any ? read.seconds(cols) : null;
}
function layerIds(entries: unknown[]): string[] {
  const out: string[] = [];
  for (const entry of entries) {
    const e = record(entry);
    const id = upper(typeof entry === 'string' ? entry : (record(e?.items)?.item_id ?? e?.item_id ?? e?.ability_id ?? ''));
    if (uuidRe.test(id)) out.push(id);
  }
  return out;
}

/** §3.1 plainness: every removal side effect the browser cannot model becomes a gate. */
function plainnessGates(status: Row, def: Row, loser: Row | null, read: ExpiryReaders, connectedBlocked: boolean): string[] {
  // A departed holder has no local contribution or loss hook to reverse. Connection
  // and zone lifecycles can still affect resident peers, so retain those gates.
  if (!loser && !flag(status, 'status_aoe_source') && !LINK_STATUS_IDS.has(statusItemId(status)))
    return connectedBlocked ? ['status-connected-peers'] : [];
  if (read.entries(def, 'maintained_by_ability').length) return ['maintained-expiry'];
  const gates: string[] = [];
  const itemId = statusItemId(status);
  if (flag(status, 'status_aoe_source')) gates.push('status-zone-anchor');
  if (upper(scalar(record(def.items)?.item_type) ?? def.item_type) === 'CREATURE') gates.push('status-shapeshift');
  if (LINK_STATUS_IDS.has(itemId) && (!EXPIRY_LINK_STATUS_IDS.has(itemId)
    || !upper(scalar(status.status_connected_id)) || connectedBlocked)) gates.push('status-link-family');
  if (loser && read.carriesLossTrigger(loser)) gates.push('status-loss-trigger-carrier');
  // End Turn cannot retire a row whose caster projection is unavailable. Keep it
  // out of the compiler's candidate set as well as the eventual save set.
  if (read.immunityExpiry && !encounterCreatureField(read.rowFor(statusCasterId(status))?.encounter_creatures, 'encounter_creature_id')) gates.push('third-party-row-missing');
  if (connectedBlocked) gates.push('status-connected-peers');
  const layers = record(def.item_layer_types) ?? {};
  const detonating = Number(scalar(layers[DAMAGE_ON_STATUS_EXPIRY]) ?? 0) !== 0;
  const keys = statusRuntimeLayerKeys(def);
  const special = (key: string): boolean => isAttackPassiveWindow(def) || isDamageOutputStatus(def)
    || key === 'passive_ability' && runtimeOnlyStatusPassives(def, read.definition, read.entries)
    || isTimedPassiveSpeedLayer(def, key, read.value)
    || (detonating && DETONATION_CHASSIS_LAYERS.has(key)) || !!(read.immunityExpiry && STATUS_DEFENSE_LAYERS.has(key));
  const footprint = keys.find(key => !isPlainDefinitionLayer(key) && !special(key));
  if (footprint !== undefined) gates.push('status-compiled-footprint:' + footprint);
  return gates;
}

/** A status row's caster id. The compiled blob ELIDES `status_caster_id` on a SELF-CAST row (the v33 intern in
 *  `action_truth_commit_inc.php` and the lean entry the A1 lane installs), and the compile reader
 *  (`creature-status-reader.ts`) defaults it to the target — the same rule here, or a self-cast Dodge on the next
 *  actor ships an empty caster id and the endpoint refuses the whole boundary (packet-1 attempt 1, 2026-09-09). */
export function statusCasterId(status: Row): string {
  const own = upper(scalar(status.status_caster_id) ?? '');
  return own !== '' ? own : upper(scalar(status.status_target_id) ?? '');
}

/** Normalized raw identity/CAS columns, including the compiled blob's omitted defaults. */
export function statusRemovalBefore(status: Row, holder = ''): Row {
  // The page reader restores an elided self-caster, preserving explicit environmental '' / null.
  const target = upper(scalar(status.status_target_id)) || upper(holder);
  const before = { status_target_id: target, status_item_id: statusItemId(status),
    status_caster_id: upper(scalar(readStatusCasterId(status, target))), status_connected_id: String(scalar(status.status_connected_id) ?? ''),
    status_deleted: Number(scalar(status.status_deleted) ?? 0) };
  for (const value of [before.status_target_id, before.status_item_id, before.status_caster_id, before.status_connected_id])
    if (value.length > 36) throw new HandBack('status-before:invalid-identity');
  if (before.status_deleted !== 0 && before.status_deleted !== 1) throw new HandBack('status-before:invalid-deleted');
  return before;
}

function removal(status: Row, def: Row, family: ExpiryFamily, logKey: ExpiryLogKey, logBy: string, allConnections: boolean, read: ExpiryReaders): StatusRemoval {
  return {
    status_id: upper(scalar(status.status_id)), target_id: upper(scalar(status.status_target_id)), caster_id: statusCasterId(status),
    item_id: statusItemId(status), family, log_key: logKey, log_by: logBy, all_connections: allConnections,
    skill_adv_ids: layerIds(read.entries(def, 'skill_roll_adv')), skill_dis_ids: layerIds(read.entries(def, 'skill_roll_dis')),
  };
}

/**
 * Discover individually due, supported candidates before deciding their peer closures. Only then run the legacy-ordered walk,
 * including immunity recompilation and retained START overrides. Callers serve boundaries and disclose gated effects.
 */
export function expirableStatuses(rows: Row[], boundary: ExpiryBoundary, read: ExpiryReaders, exclude: ReadonlySet<string> = new Set()): ExpiryResult {
  rows = [...new Set([...rows, ...(read.inventoryRows?.() ?? [])])];
  const byId = new Map([...read.allStatusRows(), ...(read.zoneStatusRows?.() ?? [])].filter(s => !flag(s, 'status_deleted'))
    .map(s => {
      const id = upper(scalar(s.status_id));
      // A target projection is authoritative when a caster still carries an older clock/flag copy.
      const target = read.rowFor(upper(scalar(s.status_target_id)));
      return [id, statusRows(target, 'target').find(s => upper(scalar(s.status_id)) === id) ?? s] as const;
    }));
  const connections = new Map<string, Row[]>();
  for (const status of byId.values()) {
    const connection = upper(scalar(status.status_connected_id));
    if (connection) connections.set(connection, [...(connections.get(connection) ?? []), status]);
  }
  const seed = walkExpiry(rows, boundary, read, exclude);
  const seeded = new Set(seed.removals.map(r => r.status_id));
  const peers: Row[] = [], forced = new Set<string>();
  for (const group of connections.values()) {
    if (!group.some(s => LINK_STATUS_IDS.has(statusItemId(s)))) continue;
    const anchorGone = group.some(s => statusItemId(s) === STATUS_IDS.concentrating && exclude.has(upper(scalar(s.status_id))));
    if (!anchorGone && !group.some(s => seeded.has(upper(scalar(s.status_id))))) continue;
    peers.push(...group);
    if (anchorGone) group.forEach(s => forced.add(upper(scalar(s.status_id))));
  }
  const candidates = peers.length ? walkExpiry(rows, boundary, read, exclude, undefined, undefined, peers, forced) : seed;
  const admitted = new Set(candidates.removals.map(r => r.status_id));
  // Preserve the original peer gate on unplain due rows too, including a connection with NO admissible candidates.
  const blocked = new Set([...connections.values()].filter(peers => peers.length > 1).flat()
    .map(s => upper(scalar(s.status_id))).filter(id => !admitted.has(id) && !exclude.has(id)));
  const closures: Set<string>[] = [];
  for (const r of candidates.removals) {
    const status = byId.get(r.status_id);
    const connection = upper(scalar(status?.status_connected_id));
    const def = status && statusDefinition(status, read);
    const additionalLayer = def && statusRuntimeLayerKeys(def).some(key => !BASE_PLAIN_DEFINITION_LAYERS.has(key) && isPlainDefinitionLayer(key));
    if (!connection) { if (additionalLayer) closures.push(new Set([r.status_id])); continue; }
    const peers = statusConnectionPeers(status!, [...byId.values()]);
    const linked = peers.some(s => LINK_STATUS_IDS.has(statusItemId(s)));
    const closure = new Set(peers.filter(s => linked || r.all_connections || upper(scalar(s.status_target_id)) === r.target_id)
      .map(s => upper(scalar(s.status_id))));
    closure.add(r.status_id);
    if ([...closure].some(id => !admitted.has(id) && !exclude.has(id))) {
      closure.forEach(id => blocked.add(id));
    }
    if (peers.length > 1 || additionalLayer) closures.push(closure);
  }
  // Closures can overlap (a caster-START row spans targets). Merge before propagating a failed member.
  const groups: Set<string>[] = [];
  for (const closure of closures) {
    for (let i = groups.length - 1; i >= 0; i--) if ([...groups[i]!].some(id => closure.has(id))) {
      groups[i]!.forEach(id => closure.add(id)); groups.splice(i, 1);
    }
    groups.push(closure);
  }
  const propagate = (): void => { for (const group of groups) if ([...group].some(id => blocked.has(id))) group.forEach(id => blocked.add(id)); };
  propagate();
  const failures = new Set<string>();
  for (;;) {
    const groupMembers = new Map<string, StatusRemoval[]>();
    for (const group of groups) if (![...group].some(id => blocked.has(id))) {
      const members = candidates.removals.filter(r => group.has(r.status_id));
      for (const member of members) groupMembers.set(member.status_id, members);
    }
    const result = walkExpiry(rows, boundary, read, exclude, blocked, groupMembers, peers, forced);
    const removed = new Set(result.removals.map(r => r.status_id));
    const split = groups.filter(group => ![...group].some(id => blocked.has(id))
      && [...group].some(id => admitted.has(id) && !removed.has(id)));
    if (!split.length) {
      failures.forEach(reason => { if (!result.gates.includes(reason)) result.gates.push(reason); });
      const lifted = groups.filter(group => [...group].some(id => removed.has(id)))
        .map(group => candidates.removals.filter(r => group.has(r.status_id)).map(r => r.status_id));
      return lifted.length ? { ...result, groups: lifted } : result;
    }
    // A defense projection can fail after closure admission. Retain its whole group, then derive locks again.
    result.gates.filter(reason => reason.startsWith('status-immunity-expiry:')).forEach(reason => failures.add(reason));
    split.forEach(group => group.forEach(id => blocked.add(id))); propagate();
  }
}

function walkExpiry(rows: Row[], boundary: ExpiryBoundary, read: ExpiryReaders, exclude: ReadonlySet<string>, blocked?: ReadonlySet<string>,
  groupMembers?: ReadonlyMap<string, readonly StatusRemoval[]>, peers: readonly Row[] = [], forced: ReadonlySet<string> = new Set()): ExpiryResult {
  const removals: StatusRemoval[] = [];
  // The observing GM owns due zone connections (and, on the calendar, due concentration). Plain expiry neither removes nor
  // skip-logs the same outcome first.
  const cascadeConnections = new Set([...read.allStatusRows(), ...(read.zoneStatusRows?.() ?? [])].filter(status => {
    if (boundary.kind === 'calendar' && isDueConcentration(status, read)) return true;
    const end = statusEndSeconds(status, read);
    return flag(status, 'status_aoe_source') && !statusPaused(status) && end !== null && end <= read.nowSeconds;
  })
    .map(status => upper(scalar(status.status_connected_id))).filter(Boolean));
  /** Rows an EARLIER pass already removed (legacy mutates the arrays as it goes): never re-read them here (Codex A review F2). */
  const inventoryTarget = (status: Row): boolean => !!readInventoryField(read.rowFor(upper(scalar(status.status_target_id))) ?? {}, 'inventory_id');
  const gone = (status: Row): boolean => !inventoryCandidate(status) || flag(status, 'status_deleted') || exclude.has(upper(scalar(status.status_id)))
    || cascadeConnections.has(upper(scalar(status.status_connected_id))) || removals.some(r => r.status_id === upper(scalar(status.status_id)));
  const gates: string[] = [];
  const overrides: ExpiryOverrides = { no_actions: false, no_reactions: false };
  const gate = (reason: string, status?: Row): void => {
    // Unsupported inventory rows keep their pre-inventory-expiry outcome: retained, without skipping creature work.
    if (status && inventoryTarget(status)) {
      const index = removals.findIndex(r => r.status_id === upper(scalar(status.status_id)));
      if (index >= 0) removals.splice(index, 1);
      return;
    }
    if (!gates.includes(reason)) gates.push(reason);
  };
  // A nested definition counts when it carries its identity even with NO layers (`item_layer_types: []` on a layer-less marker);
  // plainnessGates reads `record(def.item_layer_types) ?? {}`, so a layer-less definition is plain (T4c-2, 2026-09-11).
  const defOf = (status: Row): Row | null => statusDefinition(status, read)
    ?? (!read.rowFor(upper(scalar(status.status_target_id))) ? {} : null);
  const targetCopy = (status: Row): Row => statusRows(read.rowFor(upper(scalar(status.status_target_id))), 'target')
    .find(s => upper(scalar(s.status_id)) === upper(scalar(status.status_id))) ?? status;
  const due = (status: Row): boolean => {
    const target = isWaterWet(status) ? read.rowFor(upper(status.status_target_id)) : null;
    if (target && read.waterWetHeld?.(target)) return false;
    const end = statusEndSeconds(status, read); return end !== null && read.nowSeconds >= end;
  };
  // START flags remain boundary-driven; END/calendar inventory expiry must reach its recorded end time.
  const inventoryCandidate = (status: Row): boolean => !inventoryTarget(status) || due(status)
    || boundary.kind === 'start' && statusCasterId(status) === upper(boundary.actorId)
      && (flag(status, 'status_ends_on_caster_start_of_next_turn') || flag(status, 'status_ends_on_target_start_of_turn'));
  const admit = (status: Row, def: Row, loser: Row | null, family: ExpiryFamily, logKey: ExpiryLogKey, logBy: string, all: boolean): void => {
    const g = plainnessGates(status, def, loser, read, blocked?.has(upper(scalar(status.status_id))) ?? false);
    if (g.length) { g.forEach(reason => gate(reason, status)); return; }
    if (removals.some(r => r.status_id === upper(scalar(status.status_id)))) return;
    const result = removal(status, def, family, logKey, logBy, all, read);
    if (blocked && loser) {
      const prior = statusRows(loser, 'target').filter(s => exclude.has(upper(scalar(s.status_id)))).map(s => upper(scalar(s.status_id)));
      const together = groupMembers?.get(result.status_id)?.filter(r => r.target_id === result.target_id) ?? [];
      const ids = [...new Set([...prior, ...removals.filter(r => r.target_id === result.target_id).map(r => r.status_id),
        result.status_id, ...together.map(r => r.status_id)])];
      const runtime = statusRemovalRuntime(loser, ids, read.definition);
      if (!runtime.ok) { gate('status-compiled-footprint:' + runtime.layer, status); return; }
    }
    if (loser && changesStatusAttackBudget(def)) {
      const prior = statusRows(loser, 'target').filter(s => exclude.has(upper(scalar(s.status_id))))
        .map(s => ({ status_id: upper(scalar(s.status_id)), item_id: statusItemId(s) }));
      const steps = [...new Map([...prior, ...removals.filter(r => r.target_id === result.target_id), result]
        .map(r => [r.status_id, { kind: 'remove' as const, statusId: r.status_id, statusItemId: r.item_id }])).values()];
      const projection = projectStatusAttackBudget(loser, steps, read.definition);
      if (!projection.ok) {
        const name = String(readItemField(record(loser.player_characters), 'player_name') ?? encounterCreatureField(loser.encounter_creatures, 'encounter_creature_name')
          ?? readItemField(loser, 'item_name') ?? 'Creature');
        gate(name + ': status-attack-budget-expiry:' + projection.reason, status); return;
      }
      result.attack_budget = projection.budget;
    }
    if (blocked && read.immunityExpiry && loser && [...STATUS_DEFENSE_LAYERS].some(key => read.entries(def, key).length)) {
      const prior = statusRows(loser, 'target').filter(s => exclude.has(upper(s.status_id)))
        .map(s => ({ status_id: upper(s.status_id), item_id: statusItemId(s) }));
      const together = groupMembers?.get(result.status_id)?.filter(r => r.target_id === result.target_id) ?? [];
      const ids = [...new Set([...prior, ...removals.filter(r => r.target_id === result.target_id), result, ...together].map(r => r.status_id))];
      const projected = projectStatusDefenseRemoval(loser, ids, read);
      if (!projected.ok) { gate('status-immunity-expiry:' + projected.reason, status); return; }
      result.defenses = projected.defenses;
    }
    removals.push(result);
  };

  const inventoryTargets = (read.inventoryRows?.() ?? []).flatMap(holder => statusRows(holder, 'target'));
  for (const row of rows) {
    const rowId = upper(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id'));
    const inventoryId = upper(readInventoryField(row, 'inventory_id'));
    // Explicit target-turn flags use the caster's matching boundary for a non-turn-taking item.
    // Unflagged durations use the campaign clock at every boundary, regardless of who carries it.
    const targetBoundary = (status: Row): boolean => !inventoryId || boundary.kind !== 'calendar' && statusCasterId(status) === upper(boundary.actorId);
    const casterRows = (): Row[] => statusRows(row, 'caster').concat(inventoryTargets)
      .map(targetCopy).filter(s => statusCasterId(s) === rowId);
    if (boundary.kind === 'end' && (rowId === upper(boundary.actorId) || inventoryId)) {
      // a. caster rows — caster-END (flag AND the clock), then the maintained-ability upkeep is a gate.
      for (const status of inventoryId ? [] : casterRows()) {
        if (statusPaused(status) || gone(status)) continue;
        if (!due(status) || !flag(status, 'status_ends_on_caster_end_of_next_turn')) continue;
        const def = defOf(status);
        if (!def) { gate('definition-missing:' + statusItemId(status), status); continue; }
        const targetId = upper(scalar(status.status_target_id));
        admit(status, def, read.rowFor(targetId), 'caster_end', 'status_effect_expired', read.rowFor(targetId) ? targetId : rowId, false);
      }
      // c. target rows in legacy order: target-END → (campaign-handled skip) → calendar → DoT → contest retry; then transitional.
      for (const status of statusRows(row, 'target')) {
        if (statusPaused(status) || gone(status)) continue;
        const itemId = statusItemId(status);
        const def = defOf(status);
        if (!def) { gate('definition-missing:' + itemId, status); continue; }
        if (flag(status, 'status_ends_on_target_end_of_turn')) {
          if (!targetBoundary(status)) continue;
          admit(status, def, row, 'target_end', 'effect_ends_on_target_end_of_turn', inventoryId ? statusCasterId(status) : rowId, false);
          // R-176: a detonating definition rolls its damage on this expiry — the tick lane's work.
          if (Number(scalar(read.value(def, DAMAGE_ON_STATUS_EXPIRY)) ?? 0) !== 0) {
            const admitted = removals.find(r => r.status_id === upper(scalar(status.status_id)));
            if (admitted) { admitted.detonates = true; gate('expiry-detonation', status); }
          }
          continue;
        }
        if (CAMPAIGN_HANDLED_STATUS_IDS.has(itemId)) continue;
        const clear = !flag(status, 'status_ends_on_caster_end_of_next_turn') && !flag(status, 'status_ends_on_caster_start_of_next_turn') && !flag(status, 'status_ends_on_target_start_of_turn');
        if (itemId === STATUS_IDS.suffocating && clear) { gate('out-of-breath-status', status); continue; }
        if (due(status) && clear) {
          if (itemId === STATUS_IDS.drowning) { gate('out-of-breath-status', status); continue; }
          admit(status, def, row, 'calendar_end', 'status_effect_expired', inventoryId ? statusCasterId(status) : rowId, false); continue;
        }
        if (inventoryId) continue; // Items have no creature DoT/contest/transitional turn duties.
        const rollType = upper(read.value(def, 'roll_type'));
        const contestRetry = read.value(def, 'target_retries_contesting_roll_on_end_of_turn');
        if (rollType === DOT_ROLL_TYPE && (contestRetry == null || contestRetry === '' || Number(contestRetry) === 0)) { gate('dot-tick'); continue; }
        if (contestRetry != null && contestRetry !== '' && Number(contestRetry) !== 0) gate('contest-retry');
        if (TRANSITIONAL_STATUS_IDS.has(itemId)) gate('transitional-status');
      }
    }
    if (boundary.kind === 'start' && (rowId === upper(boundary.actorId) || inventoryId)) {
      // e. caster rows — caster-START, every connection on every target.
      for (const status of inventoryId ? [] : casterRows()) {
        if (statusPaused(status) || gone(status)) continue;
        if (!flag(status, 'status_ends_on_caster_start_of_next_turn')) continue;
        const def = defOf(status);
        if (!def) { gate('definition-missing:' + statusItemId(status), status); continue; }
        admit(status, def, read.rowFor(upper(scalar(status.status_target_id))), 'caster_start', 'status_effect_expired', rowId, true);
      }
      // f. target rows: dying first (dice), then target-START → (campaign-handled skip) → calendar → regen → overrides.
      const targets = statusRows(row, 'target').filter(s => !gone(s));
      if (!inventoryId && targets.some(s => statusItemId(s) === STATUS_IDS.dying)) gate('dying-actor');
      const v = inventoryId ? { current: 0, max: 0 } : read.vitality(row);
      for (const status of targets) {
        if (statusPaused(status)) continue;
        const itemId = statusItemId(status);
        const def = defOf(status);
        if (!def) { gate('definition-missing:' + itemId, status); continue; }
        if (flag(status, 'status_ends_on_target_start_of_turn')) {
          if (targetBoundary(status)) admit(status, def, row, 'target_start', 'effect_ends_on_target_start_of_turn', inventoryId ? statusCasterId(status) : rowId, false);
          continue;
        }
        if (CAMPAIGN_HANDLED_STATUS_IDS.has(itemId)) continue;
        const clear = !flag(status, 'status_ends_on_caster_start_of_next_turn') && !flag(status, 'status_ends_on_caster_end_of_next_turn') && !flag(status, 'status_ends_on_target_end_of_turn');
        if (due(status) && clear) { admit(status, def, row, 'calendar_start', 'status_effect_expired', inventoryId ? statusCasterId(status) : rowId, false); continue; }
        if (inventoryId) continue; // Inventory statuses cannot lock the next creature's actions or reactions.
        // Regeneration: the status's own roll_type, or its ORIGIN ability's (resolved lazily by PHP; unresolvable here ⇒ a
        // gate only when a heal could land, i.e. below maximum vitality — ft_creature_cannot_regenerate + the max check).
        const rollType = upper(read.value(def, 'roll_type'));
        if (rollType === REGENERATION_ROLL_TYPE || rollType === TEMP_REGENERATION_ROLL_TYPE) gate('regeneration-source');
        else if (v.current < v.max) {
          const originId = statusOriginDefinitionId(status);
          if (originId && originId !== itemId) {
            const origin = read.definition(originId);
            if (!origin) gate('regen-origin-unresolved:' + originId);
            else if ([REGENERATION_ROLL_TYPE, TEMP_REGENERATION_ROLL_TYPE].includes(upper(read.value(origin, 'roll_type')))) gate('regeneration-source');
          }
        }
        if (Number(read.value(def, 'no_actions') ?? 0) !== 0) overrides.no_actions = true;
        if (Number(read.value(def, 'no_reactions') ?? 0) !== 0) overrides.no_reactions = true;
      }
    }
    if (boundary.kind === 'calendar') {
      for (const status of statusRows(row, 'target')) {
        if (statusPaused(status) || gone(status)) continue;
        const itemId = statusItemId(status);
        if (CAMPAIGN_HANDLED_STATUS_IDS.has(itemId)) continue;
        const def = defOf(status);
        if (!def) { gate('definition-missing:' + itemId, status); continue; }
        const clear = !flag(status, 'status_ends_on_caster_start_of_next_turn') && !flag(status, 'status_ends_on_caster_end_of_next_turn') && !flag(status, 'status_ends_on_target_end_of_turn') && !flag(status, 'status_ends_on_target_start_of_turn');
        if (!due(status) || !clear) continue;
        if (itemId === STATUS_IDS.drowning || itemId === STATUS_IDS.suffocating) { gate('out-of-breath-status', status); continue; }
        admit(status, def, row, 'calendar', 'status_effect_expired', inventoryId ? statusCasterId(status) : rowId, false);
      }
    }
  }
  // A linked peer on another holder can be calendar-due at this same boundary. Its own future
  // turn flag/paused clock still holds the complete connection. A retired anchor ends its riders.
  for (const status of peers) {
    if (gone(status)) continue;
    const force = forced.has(upper(scalar(status.status_id)));
    if (!force && (statusPaused(status) || !due(status) || ['status_ends_on_caster_start_of_next_turn',
      'status_ends_on_caster_end_of_next_turn', 'status_ends_on_target_end_of_turn', 'status_ends_on_target_start_of_turn'].some(key => flag(status, key)))) continue;
    if (CAMPAIGN_HANDLED_STATUS_IDS.has(statusItemId(status))) continue;
    const def = defOf(status);
    if (!def) { gate('definition-missing:' + statusItemId(status), status); continue; }
    const target = upper(scalar(status.status_target_id));
    const family = boundary.kind === 'end' ? 'calendar_end' : boundary.kind === 'start' ? 'calendar_start' : 'calendar';
    admit(status, def, read.rowFor(target), family, 'status_effect_expired', target, false);
  }
  if (boundary.kind === 'start' && peers.length) {
    overrides.no_actions = false; overrides.no_reactions = false;
    for (const row of rows) if (upper(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id')) === upper(boundary.actorId)) {
      for (const status of statusRows(row, 'target')) {
        if (gone(status) || statusPaused(status)) continue;
        const def = defOf(status); if (!def) continue;
        if (Number(read.value(def, 'no_actions') ?? 0) !== 0) overrides.no_actions = true;
        if (Number(read.value(def, 'no_reactions') ?? 0) !== 0) overrides.no_reactions = true;
      }
    }
  }
  return { removals, gates, overrides };
}

/** Remove one status from BOTH projections of a row in place (the compiled shape; ft_status_target_unset twin). */
export function removeStatusFromRow(row: Row, statusId: string): boolean {
  const statuses = record(row.statuses);
  if (!statuses) return false;
  let removed = false;
  for (const mode of ['target', 'caster'] as const) {
    const stored = statuses[mode];
    if (stored === null || typeof stored !== 'object') continue;
    const single = record(stored);
    const list = single && 'status_id' in single ? [single] : Object.values(stored);
    const kept = list.filter(entry => upper(record(entry)?.status_id) !== upper(statusId));
    if (kept.length !== list.length) { statuses[mode] = kept; removed = true; }
  }
  return removed;
}
